import { CheckCircle2, ArrowRight } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Button } from "../../../components/ui/button"

const agronomieCtx = require.context("../../../assets/Agronomie", false, /\.(jpg|jpeg|png|webp)$/i)
const getImg = (s) => (typeof s === "string" ? s : s?.default ?? s)
const DEVRURAL_HERO = agronomieCtx.keys().length > 2 ? getImg(agronomieCtx(agronomieCtx.keys()[2])) : agronomieCtx.keys().length > 0 ? getImg(agronomieCtx(agronomieCtx.keys()[0])) : null

export default function DevRural() {
    const navigate = useNavigate()
    return (
        <div className="min-h-screen bg-background font-sans text-foreground antialiased">
            {/* Hero Section — Master DVR, dispensé par la FSAV */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    {DEVRURAL_HERO && <img src={DEVRURAL_HERO} alt="FSAV - Master Développement Rural" className="w-full h-full object-cover opacity-40 mix-blend-luminosity scale-105" />}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>

                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">
                                Master FSAV · DVR
                            </span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Formation d'Excellence</span>
                        </div>

                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">L'Excellence au service du</span>
                            <span className="block text-white mt-2">Développement Rural</span>
                        </h1>

                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Formez-vous aux enjeux du développement rural durable et contribuez à l'amélioration des conditions de vie des communautés.
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl">
                                Déposer ma Candidature
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                            <Button variant="ghost" className="text-white border-2 border-white/20 hover:bg-white/10 hover:border-white px-10 py-7 text-sm font-black rounded-xl backdrop-blur-sm">
                                Télécharger la brochure
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Quick Stats Bar */}
            <section className="bg-primary/5 border-b border-border/50">
                <div className="container py-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 stagger-child">
                        {[
                            { val: "2 Ans", label: "Durée" },
                            { val: "120", label: "Crédits ECTS" },
                            { val: "6 Mois", label: "Stage Inclus" },
                            { val: "Bac+3", label: "Niveau Requis" }
                        ].map((stat, i) => (
                            <div key={i} className="text-left group">
                                <div className="text-2xl font-black text-primary mb-0.5 transition-smooth group-hover:scale-110">{stat.val}</div>
                                <div className="text-[10px] text-muted-foreground font-black uppercase tracking-widest">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Introduction */}
            <section className="section-padding bg-background">
                <div className="container">
                    <div className="max-w-3xl mx-auto text-center">
                        <span className="text-accent font-black uppercase tracking-widest text-[10px] mb-4 block">À propos</span>
                        <p className="text-xs font-bold text-primary mb-4 inline-block px-3 py-1 rounded-full bg-primary/10 border border-primary/30">Dispensé par la FSAV</p>
                        <h2 className="heading-2 text-foreground mb-6">Objectifs du Programme</h2>
                        <div className="space-y-5">
                            <p className="text-sm md:text-base text-muted-foreground leading-relaxed font-medium">
                                Ce master forme des professionnels capables d'analyser les dynamiques rurales et de concevoir des projets de développement durable adaptés aux contextes locaux.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Detailed Description */}
            <section className="section-padding-sm bg-secondary/20">
                <div className="container">
                    <div className="max-w-4xl mx-auto space-y-12">
                        <div className="grid md:grid-cols-2 gap-10">
                            <div>
                                <h3 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                    <div className="w-1 h-6 bg-accent rounded-full"></div>
                                    Compétences
                                </h3>
                                <ul className="space-y-4">
                                    {[
                                        "Analyser les systèmes agraires",
                                        "Gérer des projets ruraux",
                                        "Planification et évaluation"
                                    ].map((skill, i) => (
                                        <li key={i} className="flex items-start gap-3">
                                            <CheckCircle2 className="w-4 h-4 text-accent mt-1 flex-shrink-0" />
                                            <span className="text-xs text-muted-foreground font-medium">{skill}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div>
                                <h3 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                    <div className="w-1 h-6 bg-accent rounded-full"></div>
                                    Modules Clés
                                </h3>
                                <div className="grid gap-3">
                                    {[
                                        "Économie rurale",
                                        "Ressources naturelles",
                                        "Sociologie rurale",
                                        "Politiques agricoles"
                                    ].map((mod, i) => (
                                        <div key={i} className="flex items-center gap-3 text-xs text-muted-foreground font-medium p-3 bg-background border border-border rounded-sm">
                                            <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                                            <span>{mod}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Perspectives */}
            <section className="section-padding bg-background">
                <div className="container max-w-4xl text-center">
                    <h2 className="heading-2 mb-8">Perspectives de Carrière</h2>
                    <div className="flex flex-wrap justify-center gap-3">
                        {[
                            "Chargé de Projet",
                            "Consultant",
                            "Agent de Coopération",
                            "Responsable ONG"
                        ].map((job, i) => (
                            <span key={i} className="px-4 py-2 bg-secondary/30 text-primary font-bold text-[10px] uppercase tracking-wider rounded-full">
                                {job}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section-padding bg-primary text-white">
                <div className="container text-center max-w-2xl">
                    <h2 className="heading-2 mb-6">
                        Prêt à transformer les communautés ?
                    </h2>
                    <p className="text-sm text-white/70 mb-10 font-medium lowercase">
                        Déposez votre candidature pour le master développement rural.
                    </p>
                    <div className="flex justify-center">
                        <Button onClick={() => navigate('/admission')} className="bg-accent text-accent-foreground px-8 py-3 text-base font-bold hover:bg-accent/90 transition-smooth group rounded-md shadow-xl border-b-2 border-black/10">
                            Déposer ma candidature
                            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    )
}
